/**
 * Uses WordPress REST API with nonce authentication via wpApiSettings.
 */

const API_NAMESPACE = '/wp-json/gsd-now/v1';

/**
 * Make an authenticated API request
 * @param {string} endpoint - The API endpoint (without namespace)
 * @param {object} options - Fetch options
 * @returns {Promise<object>} - The response data
 */
export async function apiRequest(endpoint, options = {}) {
  const url = `${API_NAMESPACE}${endpoint}`;

  const headers = {
    'Content-Type': 'application/json',
    'X-WP-Nonce': window.wpApiSettings?.nonce || '',
    ...options.headers,
  };

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const apiError = errorData.error || {};
    const error = new Error(apiError.message || errorData.message || 'API request failed');
    error.status = response.status;
    error.code = apiError.code || errorData.code;
    error.errors = apiError.fields || errorData.errors || {};
    throw error;
  }

  if (response.status === 204) {
    return null;
  }

  return response.json();
}

/**
 * Upload a file via multipart/form-data
 * @param {string} endpoint - The API endpoint (without namespace)
 * @param {FormData} formData - The form data containing the file
 * @returns {Promise<object>} - The response data
 */
export async function apiUpload(endpoint, formData) {
  const url = `${API_NAMESPACE}${endpoint}`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'X-WP-Nonce': window.wpApiSettings?.nonce || '',
    },
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const apiError = errorData.error || {};
    const error = new Error(apiError.message || errorData.message || 'Upload failed');
    error.status = response.status;
    error.code = apiError.code || errorData.code;
    error.errors = apiError.fields || errorData.errors || {};
    throw error;
  }

  return response.json();
}
